import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import SaveIcon from '@mui/icons-material/Save';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import FormControl from '@mui/material/FormControl';
import IconButton from '@mui/material/IconButton';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import type React from 'react';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useGetCarQuery, useUpdateCarMutation } from 'services/api/cars.api';
import { type Car, CarTypes, FuelTypes, Transmissions } from 'services/types/car';
import { SHADOW, SURFACE, SURFACE_CONTAINER_LOW, SURFACE_CONTAINER_LOWEST } from 'styles/tokens';

const EditCar: React.FC = () => {
  const { carId } = useParams<{ carId: string }>();
  const navigate = useNavigate();
  const [formData, setFormData] = useState<Partial<Car>>({});
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { data: car, isLoading: isLoadingCar } = useGetCarQuery(parseInt(carId || '0', 10));
  const [updateCar] = useUpdateCarMutation();

  useEffect(() => {
    if (car) {
      setFormData({
        referenceId: car.referenceId || '',
        brand: car.brand || '',
        model: car.model || '',
        version: car.version || '',
        year: car.year || undefined,
        km: car.km || undefined,
        price: car.price || undefined,
        secondaryPrice: car.secondaryPrice || null,
        image: car.image || '',
        transmission: car.transmission || undefined,
        carType: car.carType || undefined,
        fuelType: car.fuelType || undefined,
        branchOfficeDirection: car.branchOfficeDirection || '',
        color: car.color || '',
        link: car.link || '',
        monthlyFee: car.monthlyFee || null,
        licensePlate: car.licensePlate || '',
        technicalSheet: car.technicalSheet || '',
        originalImageUrl: car.originalImageUrl || '',
        promotions: car.promotions || '',
      });
    }
  }, [car]);

  const handleFieldChange = (field: keyof Car, value: string | number | boolean | null | undefined) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
    setError(null);
  };

  const navigateToCars = () => {
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate('/cars');
    }
  };

  const handleSubmit = async () => {
    if (!carId) return;

    if (!formData.brand || !formData.model || !formData.transmission || !formData.carType || !formData.fuelType) {
      setError('Marca, modelo, transmisión, tipo de auto y tipo de combustible son requeridos');
      return;
    }
    setIsLoading(true);
    try {
      await updateCar({
        id: parseInt(carId, 10),
        body: formData,
      }).unwrap();
      navigateToCars();
    } catch (updateError) {
      console.error('Error updating car:', updateError);
      setError('Error al actualizar el auto');
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoadingCar) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!car) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <Typography variant="body1" sx={{ color: 'text.secondary' }}>
          Auto no encontrado
        </Typography>
      </Box>
    );
  }

  const imageUrl = formData.image || formData.originalImageUrl;

  return (
    <Box sx={{ backgroundColor: SURFACE, minHeight: '100vh', py: 4, px: 3 }}>
      <Box sx={{ maxWidth: 1100, mx: 'auto' }}>
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, gap: 2 }}>
          <Tooltip title="Volver">
            <IconButton onClick={navigateToCars} sx={{ color: 'text.secondary' }}>
              <ArrowBackIcon />
            </IconButton>
          </Tooltip>
          <Box>
            <Typography variant="h4">
              {car.brand} {car.model}
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              Editar información del vehículo
            </Typography>
          </Box>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Image Preview + Vehicle Info side by side */}
        <Box sx={{ display: 'flex', gap: 3, mb: 3, flexDirection: { xs: 'column', md: 'row' } }}>
          {/* Image Preview */}
          {imageUrl && (
            <Box
              sx={{
                width: { xs: '100%', md: 320 },
                height: 220,
                flexShrink: 0,
                borderRadius: 3,
                overflow: 'hidden',
                backgroundColor: SURFACE_CONTAINER_LOW,
                boxShadow: SHADOW,
              }}
            >
              <Box
                component="img"
                src={imageUrl}
                alt={`${car.brand} ${car.model}`}
                sx={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                }}
                onError={(e: React.SyntheticEvent<HTMLImageElement>) => {
                  e.currentTarget.style.display = 'none';
                }}
              />
            </Box>
          )}

          {/* Vehicle Info Card */}
          <Box
            sx={{
              flex: 1,
              backgroundColor: SURFACE_CONTAINER_LOWEST,
              borderRadius: 3,
              p: 3,
              boxShadow: SHADOW,
            }}
          >
            <Typography variant="subtitle2" sx={{ mb: 2, color: 'text.secondary' }}>
              Información del Vehículo
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 2 }}>
              <TextField
                required
                label="Marca"
                value={formData.brand || ''}
                onChange={(e) => handleFieldChange('brand', e.target.value.toUpperCase())}
                fullWidth
                placeholder="Ej: TOYOTA, FORD"
              />
              <TextField
                required
                label="Modelo"
                value={formData.model || ''}
                onChange={(e) => handleFieldChange('model', e.target.value.toUpperCase())}
                fullWidth
                placeholder="Ej: RAV4, L200"
              />
              <TextField
                label="Versión"
                value={formData.version || ''}
                onChange={(e) => handleFieldChange('version', e.target.value.toUpperCase())}
                fullWidth
                placeholder="Ej: 1.5 TDI 4x4"
              />
              <TextField
                label="Año"
                type="number"
                value={formData.year || ''}
                onChange={(e) => handleFieldChange('year', parseInt(e.target.value, 10))}
                fullWidth
              />
              <TextField
                label="Kilometraje"
                type="number"
                value={formData.km || ''}
                onChange={(e) => handleFieldChange('km', parseInt(e.target.value, 10))}
                fullWidth
              />
              <TextField
                label="Color"
                value={formData.color || ''}
                onChange={(e) => handleFieldChange('color', e.target.value.toUpperCase())}
                fullWidth
              />
              <TextField
                label="Patente"
                value={formData.licensePlate || ''}
                onChange={(e) => handleFieldChange('licensePlate', e.target.value.toUpperCase())}
                fullWidth
              />
              <TextField
                label="ID Empresa (Opcional)"
                value={formData.referenceId || ''}
                onChange={(e) => handleFieldChange('referenceId', e.target.value)}
                fullWidth
              />
            </Box>
          </Box>
        </Box>

        {/* Specs Card */}
        <Box
          sx={{
            backgroundColor: SURFACE_CONTAINER_LOWEST,
            borderRadius: 3,
            p: 3,
            mb: 3,
            boxShadow: SHADOW,
          }}
        >
          <Typography variant="subtitle2" sx={{ mb: 2, color: 'text.secondary' }}>
            Especificaciones
          </Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 2 }}>
            <FormControl fullWidth>
              <InputLabel>Transmisión</InputLabel>
              <Select
                required
                value={formData.transmission || ''}
                onChange={(e) => handleFieldChange('transmission', e.target.value)}
                label="Transmisión"
              >
                <MenuItem value="">Sin especificar</MenuItem>
                {Transmissions.map((transmission) => (
                  <MenuItem key={transmission} value={transmission}>
                    {transmission}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>Tipo de Auto</InputLabel>
              <Select
                required
                value={formData.carType || ''}
                onChange={(e) => handleFieldChange('carType', e.target.value)}
                label="Tipo de Auto"
              >
                <MenuItem value="">Sin especificar</MenuItem>
                {CarTypes.map((carType) => (
                  <MenuItem key={carType} value={carType}>
                    {carType}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>Tipo de Combustible</InputLabel>
              <Select
                required
                value={formData.fuelType || ''}
                onChange={(e) => handleFieldChange('fuelType', e.target.value)}
                label="Tipo de Combustible"
              >
                <MenuItem value="">Sin especificar</MenuItem>
                {FuelTypes.map((fuelType) => (
                  <MenuItem key={fuelType} value={fuelType}>
                    {fuelType}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </Box>

        {/* Pricing Card */}
        <Box
          sx={{
            backgroundColor: SURFACE_CONTAINER_LOWEST,
            borderRadius: 3,
            p: 3,
            mb: 3,
            boxShadow: SHADOW,
          }}
        >
          <Typography variant="subtitle2" sx={{ mb: 2, color: 'text.secondary' }}>
            Precios
          </Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 2 }}>
            <TextField
              label="Precio"
              type="number"
              required
              value={formData.price || ''}
              onChange={(e) => handleFieldChange('price', parseInt(e.target.value, 10))}
              fullWidth
              slotProps={{ input: { startAdornment: '$' } }}
            />
            <TextField
              label="Cuota Mensual (Opcional)"
              type="number"
              value={formData.monthlyFee || ''}
              onChange={(e) => handleFieldChange('monthlyFee', parseInt(e.target.value, 10))}
              fullWidth
              slotProps={{ input: { startAdornment: '$' } }}
            />
          </Box>
        </Box>

        {/* Links & Media Card */}
        <Box
          sx={{
            backgroundColor: SURFACE_CONTAINER_LOWEST,
            borderRadius: 3,
            p: 3,
            mb: 3,
            boxShadow: SHADOW,
          }}
        >
          <Typography variant="subtitle2" sx={{ mb: 2, color: 'text.secondary' }}>
            Enlaces y Medios
          </Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 2 }}>
            <TextField
              label="Sucursal"
              value={formData.branchOfficeDirection || ''}
              onChange={(e) => handleFieldChange('branchOfficeDirection', e.target.value)}
              fullWidth
            />
            <TextField
              label="Link"
              value={formData.link || ''}
              onChange={(e) => handleFieldChange('link', e.target.value)}
              fullWidth
            />
            <TextField
              label="URL de ficha técnica"
              value={formData.technicalSheet || ''}
              onChange={(e) => handleFieldChange('technicalSheet', e.target.value)}
              fullWidth
            />
            <TextField
              label="URL de Imagen"
              value={formData.image || ''}
              onChange={(e) => handleFieldChange('image', e.target.value)}
              fullWidth
            />
            <TextField
              label="URL de Imagen Original"
              value={formData.originalImageUrl || ''}
              onChange={(e) => handleFieldChange('originalImageUrl', e.target.value)}
              fullWidth
            />
          </Box>
          <Box sx={{ mt: 2 }}>
            <TextField
              label="Promociones"
              value={formData.promotions || ''}
              onChange={(e) => handleFieldChange('promotions', e.target.value)}
              fullWidth
              multiline
              rows={1}
              placeholder="Ej: Precio con financiamiento, precio con descuento, etc."
            />
          </Box>
        </Box>

        {/* Info notice — calm, not jarring */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 1.5,
            p: 2,
            mb: 3,
            backgroundColor: SURFACE_CONTAINER_LOW,
            borderRadius: 2,
          }}
        >
          <InfoOutlinedIcon sx={{ color: 'text.secondary', mt: 0.25, fontSize: '1.2rem' }} />
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            Los autos se actualizan automáticamente desde fuentes externas de datos. Los cambios manuales pueden ser
            sobrescritos en futuras actualizaciones automáticas.
          </Typography>
        </Box>

        {/* Actions — sticky bottom feel */}
        <Box
          sx={{
            display: 'flex',
            gap: 2,
            justifyContent: 'flex-end',
            p: 2,
            backgroundColor: SURFACE_CONTAINER_LOWEST,
            borderRadius: 3,
            boxShadow: SHADOW,
          }}
        >
          <Button onClick={navigateToCars} disabled={isLoading}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            color="secondary"
            startIcon={<SaveIcon />}
            onClick={() => {
              handleSubmit().catch((submitError) => console.error('Error updating car:', submitError));
            }}
            disabled={isLoading}
          >
            {isLoading ? 'Guardando...' : 'Guardar Cambios'}
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default EditCar;
